import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { fetchMaintenanceStatus, setMaintenance, updateMaintenanceMessage } from '@/api/admin.api';

export function AdminMaintenancePage() {
  const queryClient = useQueryClient();
  const [editMessage, setEditMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'maintenance'],
    queryFn: fetchMaintenanceStatus,
  });

  // Nachricht aus Query in den lokalen State übernehmen
  const prevData = data;
  if (prevData && !editingMessage && editMessage !== prevData.message && editMessage === '') {
    setEditMessage(prevData.message);
  }

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      setMaintenance(enabled, enabled ? editMessage : undefined),
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'maintenance'], updated);
      toast.success(updated.enabled ? 'Wartungsmodus aktiviert.' : 'Wartungsmodus deaktiviert.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const messageMutation = useMutation({
    mutationFn: (msg: string) => updateMaintenanceMessage(msg),
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'maintenance'], updated);
      setEditingMessage(false);
      toast.success('Nachricht gespeichert.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const isActive = data?.enabled ?? false;

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Wartungsmodus</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-2xl space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          ) : (
            <>
              {/* Status-Banner */}
              {isActive && (
                <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
                  <svg className="h-5 w-5 shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                  <div className="text-sm">
                    <p className="font-semibold text-amber-800">Wartungsmodus ist aktiv</p>
                    <p className="text-amber-700">Alle Benutzer (außer Admins) sehen eine Wartungsseite.</p>
                    {data?.enabledAt && (
                      <p className="mt-0.5 text-xs text-amber-600">
                        Aktiviert am {new Date(data.enabledAt).toLocaleString('de-DE')}
                        {data.enabledBy ? ` von ${data.enabledBy}` : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Toggle-Karte */}
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="font-semibold text-slate-900">
                      {isActive ? 'Wartungsmodus deaktivieren' : 'Wartungsmodus aktivieren'}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      {isActive
                        ? 'Die App wird wieder für alle Benutzer zugänglich.'
                        : 'Normale Benutzer sehen eine Wartungsmeldung und können sich nicht anmelden.'}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleMutation.mutate(!isActive)}
                    disabled={toggleMutation.isPending}
                    className={`self-start rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 sm:self-auto ${
                      isActive
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-amber-600 hover:bg-amber-700'
                    }`}
                  >
                    {toggleMutation.isPending
                      ? 'Bitte warten…'
                      : isActive
                      ? 'Deaktivieren'
                      : 'Aktivieren'}
                  </button>
                </div>
              </div>

              {/* Nachricht bearbeiten */}
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
                <h2 className="mb-3 font-semibold text-slate-900">Wartungsnachricht</h2>
                <p className="mb-3 text-sm text-slate-500">
                  Diese Nachricht sehen Benutzer, wenn der Wartungsmodus aktiv ist.
                </p>
                {editingMessage ? (
                  <div className="space-y-3">
                    <textarea
                      value={editMessage}
                      onChange={(e) => setEditMessage(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => messageMutation.mutate(editMessage)}
                        disabled={messageMutation.isPending}
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        Speichern
                      </button>
                      <button
                        onClick={() => { setEditingMessage(false); setEditMessage(data?.message ?? ''); }}
                        className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700 flex-1">
                      {data?.message}
                    </p>
                    <button
                      onClick={() => { setEditingMessage(true); setEditMessage(data?.message ?? ''); }}
                      className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Bearbeiten
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminMaintenancePage;
